import { EnumAccountType } from '@/components/auth/on-boarding/types'
import { MetaData } from './Firestore'
import { Timestamp, VectorValue } from 'firebase/firestore'

export enum AvailableRoles {
  ADMIN = 'admin',
  EMPLOYER_ADMIN = 'employer_admin',
  EMPLOYER_MEMBER = 'employer_member',
  LAWYER_ADMIN = 'lawyer_admin',
  LAWYER_STAFF = 'lawyer_staff',
  PLATFORM_ADMIN = 'platform_admin',
  SCHOOL_ADMIN = 'school_admin',
  STUDENT = 'student',
  SUPER_ADMIN = 'superadmin',
}

export enum Permissions {
  ADMIN_OPERATIONS_CREATE = 'admin_operations:create',
  ADMIN_OPERATIONS_DELETE = 'admin_operations:delete',
  ADMIN_OPERATIONS_READ = 'admin_operations:read',
  ADMIN_OPERATIONS_UPDATE = 'admin_operations:update',
  APPLICATIONS_CREATE = 'applications:create',
  APPLICATIONS_DELETE = 'applications:delete',
  APPLICATIONS_READ = 'applications:read',
  APPLICATIONS_UPDATE = 'applications:update',
  BILLING_CREATE = 'billing:create',
  BILLING_DELETE = 'billing:delete',
  BILLING_READ = 'billing:read',
  BILLING_UPDATE = 'billing:update',
  CASE_MANAGEMENT_CREATE = 'case_management:create',
  CASE_MANAGEMENT_DELETE = 'case_management:delete',
  CASE_MANAGEMENT_READ = 'case_management:read',
  CASE_MANAGEMENT_UPDATE = 'case_management:update',
  INSTITUTION_MANAGEMENT_CREATE = 'institution_management:create',
  INSTITUTION_MANAGEMENT_DELETE = 'institution_management:delete',
  INSTITUTION_MANAGEMENT_READ = 'institution_management:read',
  INSTITUTION_MANAGEMENT_UPDATE = 'institution_management:update',
  INTERVIEWS_CREATE = 'interviews:create',
  INTERVIEWS_DELETE = 'interviews:delete',
  INTERVIEWS_READ = 'interviews:read',
  INTERVIEWS_UPDATE = 'interviews:update',
  JOBS_CREATE = 'jobs:create',
  JOBS_DELETE = 'jobs:delete',
  JOBS_READ = 'jobs:read',
  JOBS_UPDATE = 'jobs:update',
  MESSAGES_CREATE = 'messages:create',
  MESSAGES_DELETE = 'messages:delete',
  MESSAGES_READ = 'messages:read',
  MESSAGES_UPDATE = 'messages:update',
  REPORTS_CREATE = 'reports:create',
  REPORTS_DELETE = 'reports:delete',
  REPORTS_READ = 'reports:read',
  REPORTS_UPDATE = 'reports:update',
}

export const rolePermissions: Record<AvailableRoles, Permissions[]> = {
  [AvailableRoles.STUDENT]: [
    Permissions.APPLICATIONS_CREATE,
    Permissions.APPLICATIONS_READ,
    Permissions.APPLICATIONS_UPDATE,
    Permissions.INTERVIEWS_READ,
    Permissions.INTERVIEWS_UPDATE,
    Permissions.MESSAGES_CREATE,
    Permissions.MESSAGES_READ,
    Permissions.MESSAGES_UPDATE,
    Permissions.JOBS_READ,
  ],
  [AvailableRoles.EMPLOYER_ADMIN]: [
    Permissions.JOBS_CREATE,
    Permissions.JOBS_READ,
    Permissions.JOBS_UPDATE,
    Permissions.JOBS_DELETE,
    Permissions.APPLICATIONS_CREATE,
    Permissions.APPLICATIONS_READ,
    Permissions.APPLICATIONS_UPDATE,
    Permissions.INTERVIEWS_CREATE,
    Permissions.INTERVIEWS_READ,
    Permissions.INTERVIEWS_UPDATE,
    Permissions.INTERVIEWS_DELETE,
    Permissions.MESSAGES_CREATE,
    Permissions.MESSAGES_READ,
    Permissions.MESSAGES_UPDATE,
    Permissions.INSTITUTION_MANAGEMENT_READ,
    Permissions.INSTITUTION_MANAGEMENT_UPDATE,
    Permissions.BILLING_READ,
    Permissions.BILLING_UPDATE,
    Permissions.REPORTS_READ,
  ],
  [AvailableRoles.EMPLOYER_MEMBER]: [
    Permissions.JOBS_READ,
    Permissions.JOBS_UPDATE,
    Permissions.APPLICATIONS_CREATE,
    Permissions.APPLICATIONS_READ,
    Permissions.APPLICATIONS_UPDATE,
    Permissions.INTERVIEWS_CREATE,
    Permissions.INTERVIEWS_READ,
    Permissions.INTERVIEWS_UPDATE,
    Permissions.INTERVIEWS_DELETE,
    Permissions.MESSAGES_CREATE,
    Permissions.MESSAGES_READ,
    Permissions.MESSAGES_UPDATE,
    Permissions.INSTITUTION_MANAGEMENT_READ,
    Permissions.REPORTS_READ,
  ],
  [AvailableRoles.SCHOOL_ADMIN]: [
    Permissions.JOBS_CREATE,
    Permissions.JOBS_READ,
    Permissions.JOBS_UPDATE,
    Permissions.JOBS_DELETE,
    Permissions.APPLICATIONS_CREATE,
    Permissions.APPLICATIONS_READ,
    Permissions.APPLICATIONS_UPDATE,
    Permissions.INTERVIEWS_CREATE,
    Permissions.INTERVIEWS_READ,
    Permissions.INTERVIEWS_UPDATE,
    Permissions.INTERVIEWS_DELETE,
    Permissions.MESSAGES_CREATE,
    Permissions.MESSAGES_READ,
    Permissions.MESSAGES_UPDATE,
    Permissions.INSTITUTION_MANAGEMENT_READ,
    Permissions.INSTITUTION_MANAGEMENT_UPDATE,
    Permissions.CASE_MANAGEMENT_READ,
    Permissions.BILLING_READ,
    Permissions.REPORTS_READ,
  ],
  [AvailableRoles.LAWYER_ADMIN]: [
    Permissions.MESSAGES_CREATE,
    Permissions.MESSAGES_READ,
    Permissions.MESSAGES_UPDATE,
    Permissions.MESSAGES_DELETE,
    Permissions.APPLICATIONS_READ,
    Permissions.INTERVIEWS_READ,
    Permissions.CASE_MANAGEMENT_CREATE,
    Permissions.CASE_MANAGEMENT_READ,
    Permissions.CASE_MANAGEMENT_UPDATE,
    Permissions.CASE_MANAGEMENT_DELETE,
    Permissions.BILLING_READ,
    Permissions.BILLING_UPDATE,
    Permissions.REPORTS_READ,
  ],
  [AvailableRoles.LAWYER_STAFF]: [
    Permissions.MESSAGES_CREATE,
    Permissions.MESSAGES_READ,
    Permissions.MESSAGES_UPDATE,
    Permissions.APPLICATIONS_READ,
    Permissions.INTERVIEWS_READ,
    Permissions.CASE_MANAGEMENT_READ,
    Permissions.CASE_MANAGEMENT_UPDATE,
  ],
  [AvailableRoles.PLATFORM_ADMIN]: [
    Permissions.JOBS_CREATE,
    Permissions.JOBS_READ,
    Permissions.JOBS_UPDATE,
    Permissions.JOBS_DELETE,
    Permissions.APPLICATIONS_CREATE,
    Permissions.APPLICATIONS_READ,
    Permissions.APPLICATIONS_UPDATE,
    Permissions.APPLICATIONS_DELETE,
    Permissions.INTERVIEWS_CREATE,
    Permissions.INTERVIEWS_READ,
    Permissions.INTERVIEWS_UPDATE,
    Permissions.INTERVIEWS_DELETE,
    Permissions.MESSAGES_CREATE,
    Permissions.MESSAGES_READ,
    Permissions.MESSAGES_UPDATE,
    Permissions.MESSAGES_DELETE,
    Permissions.INSTITUTION_MANAGEMENT_CREATE,
    Permissions.INSTITUTION_MANAGEMENT_READ,
    Permissions.INSTITUTION_MANAGEMENT_UPDATE,
    Permissions.INSTITUTION_MANAGEMENT_DELETE,
    Permissions.CASE_MANAGEMENT_CREATE,
    Permissions.CASE_MANAGEMENT_READ,
    Permissions.CASE_MANAGEMENT_UPDATE,
    Permissions.CASE_MANAGEMENT_DELETE,
    Permissions.BILLING_CREATE,
    Permissions.BILLING_READ,
    Permissions.BILLING_UPDATE,
    Permissions.BILLING_DELETE,
    Permissions.REPORTS_CREATE,
    Permissions.REPORTS_READ,
    Permissions.REPORTS_UPDATE,
    Permissions.REPORTS_DELETE,
    Permissions.ADMIN_OPERATIONS_CREATE,
    Permissions.ADMIN_OPERATIONS_READ,
    Permissions.ADMIN_OPERATIONS_UPDATE,
    Permissions.ADMIN_OPERATIONS_DELETE,
  ],
  [AvailableRoles.ADMIN]: [
    Permissions.JOBS_READ,
    Permissions.APPLICATIONS_READ,
    Permissions.INTERVIEWS_READ,
    Permissions.MESSAGES_READ,
    Permissions.INSTITUTION_MANAGEMENT_READ,
    Permissions.CASE_MANAGEMENT_READ,
    Permissions.BILLING_READ,
    Permissions.REPORTS_READ,
    Permissions.ADMIN_OPERATIONS_READ,
  ],
  [AvailableRoles.SUPER_ADMIN]: [
    Permissions.JOBS_CREATE,
    Permissions.JOBS_READ,
    Permissions.JOBS_UPDATE,
    Permissions.JOBS_DELETE,
    Permissions.APPLICATIONS_CREATE,
    Permissions.APPLICATIONS_READ,
    Permissions.APPLICATIONS_UPDATE,
    Permissions.APPLICATIONS_DELETE,
    Permissions.INTERVIEWS_CREATE,
    Permissions.INTERVIEWS_READ,
    Permissions.INTERVIEWS_UPDATE,
    Permissions.INTERVIEWS_DELETE,
    Permissions.MESSAGES_CREATE,
    Permissions.MESSAGES_READ,
    Permissions.MESSAGES_UPDATE,
    Permissions.MESSAGES_DELETE,
    Permissions.INSTITUTION_MANAGEMENT_CREATE,
    Permissions.INSTITUTION_MANAGEMENT_READ,
    Permissions.INSTITUTION_MANAGEMENT_UPDATE,
    Permissions.INSTITUTION_MANAGEMENT_DELETE,
    Permissions.CASE_MANAGEMENT_CREATE,
    Permissions.CASE_MANAGEMENT_READ,
    Permissions.CASE_MANAGEMENT_UPDATE,
    Permissions.CASE_MANAGEMENT_DELETE,
    Permissions.BILLING_CREATE,
    Permissions.BILLING_READ,
    Permissions.BILLING_UPDATE,
    Permissions.BILLING_DELETE,
    Permissions.REPORTS_CREATE,
    Permissions.REPORTS_READ,
    Permissions.REPORTS_UPDATE,
    Permissions.REPORTS_DELETE,
    Permissions.ADMIN_OPERATIONS_CREATE,
    Permissions.ADMIN_OPERATIONS_READ,
    Permissions.ADMIN_OPERATIONS_UPDATE,
    Permissions.ADMIN_OPERATIONS_DELETE,
  ],
}

export function hasPermission(roles: AvailableRoles[] = [], permission: Permissions): boolean {
  if (roles.includes(AvailableRoles.PLATFORM_ADMIN) || roles.includes(AvailableRoles.SUPER_ADMIN)) {
    return true
  }

  return roles.some((role) => rolePermissions[role]?.includes(permission) ?? false)
}

export function getPermissionTitle(permission: Permissions): string {
  switch (permission) {
    case Permissions.JOBS_CREATE:
    case Permissions.JOBS_READ:
    case Permissions.JOBS_UPDATE:
    case Permissions.JOBS_DELETE:
      return 'Jobs'
    case Permissions.APPLICATIONS_CREATE:
    case Permissions.APPLICATIONS_READ:
    case Permissions.APPLICATIONS_UPDATE:
    case Permissions.APPLICATIONS_DELETE:
      return 'Applications'
    case Permissions.INTERVIEWS_CREATE:
    case Permissions.INTERVIEWS_READ:
    case Permissions.INTERVIEWS_UPDATE:
    case Permissions.INTERVIEWS_DELETE:
      return 'Interviews'
    case Permissions.MESSAGES_CREATE:
    case Permissions.MESSAGES_READ:
    case Permissions.MESSAGES_UPDATE:
    case Permissions.MESSAGES_DELETE:
      return 'Messages'
    case Permissions.INSTITUTION_MANAGEMENT_CREATE:
    case Permissions.INSTITUTION_MANAGEMENT_READ:
    case Permissions.INSTITUTION_MANAGEMENT_UPDATE:
    case Permissions.INSTITUTION_MANAGEMENT_DELETE:
      return 'Institution Management'
    case Permissions.CASE_MANAGEMENT_CREATE:
    case Permissions.CASE_MANAGEMENT_READ:
    case Permissions.CASE_MANAGEMENT_UPDATE:
    case Permissions.CASE_MANAGEMENT_DELETE:
      return 'Case Management'
    case Permissions.BILLING_CREATE:
    case Permissions.BILLING_READ:
    case Permissions.BILLING_UPDATE:
    case Permissions.BILLING_DELETE:
      return 'Billing'
    case Permissions.REPORTS_CREATE:
    case Permissions.REPORTS_READ:
    case Permissions.REPORTS_UPDATE:
    case Permissions.REPORTS_DELETE:
      return 'Reports'
    case Permissions.ADMIN_OPERATIONS_CREATE:
    case Permissions.ADMIN_OPERATIONS_READ:
    case Permissions.ADMIN_OPERATIONS_UPDATE:
    case Permissions.ADMIN_OPERATIONS_DELETE:
      return 'Admin Operations'
    default:
      return ''
  }
}

// Student profile related interfaces
export interface StudentCertification {
  id: string
  issued: string
  logo: string | null
  org: string
  title: string
}

export interface StudentDocument {
  name: string
  uploadDate: string
}

export interface StudentEducationEntry {
  avatarColor?: string,
  content: string,
  courses?: string
  degree: string,
  expectedGraduation?: string,
  gpa?: string,
  period?: string,
  school: string,
  text: string
}

export interface StudentExperienceEntry {
  bullets: string[]
  company: string,
  content: string,
  duration: string,
  location?: string,
  logo: string | null,
  role: string,
  text: string
}

export interface StudentWorkAuthorization {
  expectedDate: Timestamp
  status: string
  type: string
}

export interface StudentProfile {
  aboutText: string,
  avatar: string,
  certifications: StudentCertification[],
  createdAt: Timestamp,
  documents: StudentDocument[],
  educations: StudentEducationEntry[],
  email: string,
  embedding: VectorValue,
  experiences: StudentExperienceEntry[],
  id: string,
  lastUpdatedBy: string,
  linkedIn: string,
  location: string,
  phone: string,
  professionalSummary: string,
  professionalTitle: string,
  rank: string,
  updatedAt: Timestamp,
  workAuth: StudentWorkAuthorization
}

export const studentProfileKeys = [
  // 'certifications',
  'educations',
  'email',
  'experiences',
  'linkedIn',
  'location',
  'phone',
  'professionalSummary',
  'professionalTitle',
  'workAuth',
]

export interface User {
  company?: string
  createdAt?: Timestamp,
  displayName?: string,
  email: string,
  enterprise: string,
  firstName: string,
  id?: string,
  institution?: string,
  lastName: string,
  lastUpdatedBy?: string,
  metaData: MetaData,
  photoURL?: string,
  roles?: AvailableRoles[],
  type: EnumAccountType,
  updatedAt?: Timestamp
}

export const userKeys = [
  'displayName',
  'email',
  'firstName',
  'lastName',
  'photoURL',
]
